import { useNavigate } from 'react-router';
import { useEffect, useReducer, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '@/zustand/auth-store';
import { AuthMutations } from '@/features/auth/login/services/mutation';
import { showSuccess } from '@/libs/react.toastify';
import { getHomePath } from '@/utils/helpers';
import { reducer, initialState, ACTIONS } from '@/reducers/otp-page';
import { OTP_PAGE_FORM_DEFAULT_VALUES } from './module';
import { OTP_PAGE_FORM_SCHEMA } from './schema';

export const useOtpPage = () => {
  const navigate = useNavigate();
  const { setAccessToken, setRefreshToken, setUser } = useAuth();
  const { control } = useForm({
    defaultValues: OTP_PAGE_FORM_DEFAULT_VALUES,
    resolver: yupResolver(OTP_PAGE_FORM_SCHEMA),
  });

  const [state, dispatch] = useReducer(reducer, initialState);
  const { otpLastValue, errorMessage, isFalseValue, isTrueValue, isReset, timeLeft } = state;

  const isMountedRef = useRef(true);

  const email = localStorage.getItem('email');

  useEffect(() => {
    if (timeLeft <= 0) {
      return;
    }
    const timer = setInterval(() => dispatch({ type: ACTIONS.TICK_TIMER }), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const onVerifyOtpSuccess = useCallback(
    data => {
      if (!isMountedRef.current) {
        return;
      }

      const accessToken = data?.tokens?.access ?? data?.tokens?.accessToken ?? '';
      const refreshToken = data?.tokens?.refresh ?? data?.tokens?.refreshToken ?? '';

      if (accessToken) {
        setAccessToken(accessToken);
      }
      if (refreshToken) {
        setRefreshToken(refreshToken);
      }
      if (data?.user) {
        setUser(data.user);
        localStorage.setItem('user-info', JSON.stringify(data.user));
      }

      if (data?.message) {
        showSuccess(data.message);
      }

      if (accessToken) {
        return navigate('/', { replace: true });
      }
    },
    [navigate, setAccessToken, setRefreshToken, setUser]
  );

  const onVerifyOtpError = useCallback(error => {
    if (!isMountedRef.current) {
      return;
    }
    dispatch({
      type: ACTIONS.VERIFY_ERROR,
      payload: error.response?.data?.message ?? '',
    });
  }, []);

  const onResendOtpSuccess = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }
    showSuccess('OTP has been resent successfully');
    dispatch({ type: ACTIONS.RESEND_SUCCESS });
  }, []);

  const onResendOtpError = useCallback(error => {
    if (!isMountedRef.current) {
      return;
    }
    dispatch({
      type: ACTIONS.RESEND_ERROR,
      payload: {
        errorMessage: error.response?.data?.message ?? '',
      },
    });
  }, []);

  const { mutate: verifyLoginOtp, isPending: isVerifyingLoginOtp } =
    AuthMutations.useVerifyLoginOtp({
      onSuccess: onVerifyOtpSuccess,
      onError: onVerifyOtpError,
    });
  const { mutate: resendLoginOtp, isPending: isResendingLoginOtp } =
    AuthMutations.useResendLoginOtp({
      onSuccess: onResendOtpSuccess,
      onError: onResendOtpError,
    });

  const handleVerifyOtp = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_VERIFY_UI });
    const payload = {
      otp: otpLastValue,
      email: email?.toLowerCase?.() ?? email,
    };
    verifyLoginOtp(payload);
  }, [email, otpLastValue, verifyLoginOtp]);

  const handleResendOtp = useCallback(async () => {
    dispatch({ type: ACTIONS.RESEND_START });
    const payload = {
      email: email?.toLowerCase?.() ?? email,
    };
    resendLoginOtp(payload);
  }, [email, resendLoginOtp]);

  useEffect(() => {
    if (otpLastValue.length === 6) {
      handleVerifyOtp();
    }
  }, [otpLastValue, handleVerifyOtp]);

  const isPending = isVerifyingLoginOtp || isResendingLoginOtp;

  const canResendOtp = timeLeft === 0 && !isPending;

  const setOtpLastValue = useCallback(value => {
    dispatch({ type: ACTIONS.SET_OTP_VALUE, payload: value });
  }, []);

  return {
    navigate,
    returnPath: '',
    otpLastValue,
    setOtpLastValue,
    errorMessage,
    isFalseValue,
    isTrueValue,
    isReset,
    timeLeft,
    control,
    isPending,
    canResendOtp,
    handleResendOtp,
  };
};
